import React, {
  Children, FC, UIEvent, useEffect, useRef,
  useState,
} from 'react';
import { PagerViewProps } from '~/core/dto/components/pagerViewDTO';
import arrow from '~/assets/icons/Arrow2.svg';

import './PagerView.styles.css';

const PagerView: FC<PagerViewProps> = (
  {
    children, highlightActive, useArrows, autoScroll = true, defaultIndex = 0,
  },
) => {

  const scrollRef = useRef<HTMLDivElement>( null );
  const timeout = useRef<NodeJS.Timeout | null>( null );

  const [ activeIndex, setActiveIndex ] = useState( 0 );

  const x = highlightActive ? 148 : 48;
  const y = highlightActive ? 58 : 20;

  const scroll = ( index: number, noTimer = false ) => {

    if ( timeout.current ) {

      clearTimeout( timeout.current );

    }

    timeout.current = setTimeout( () => {

      scrollRef.current?.scrollTo( {
        left: ( scrollRef.current.clientWidth - x ) * index - y,
        behavior: 'smooth',
      } );

    }, noTimer ? 0 : 3000 );

  };

  const onScroll = ( event: UIEvent<HTMLDivElement> ) => {

    const { scrollLeft } = event.currentTarget;
    const index = Math.round(
      ( scrollLeft + y ) / ( ( scrollRef.current?.clientWidth || 1 ) - x ),
    );

    if ( index !== activeIndex ) {

      setActiveIndex( index );

    }

    if ( index < Children.count( children ) - 1 && autoScroll ) {

      scroll( index + 1 );

    }

  };

  useEffect( () => {

    if ( autoScroll ) {

      scroll( 1 );

    } else if ( defaultIndex > 0 ) {

      scroll( defaultIndex, true );

    }

  }, [ autoScroll, defaultIndex ] );

  const canIncrease = activeIndex < Children.count( children ) - 1;
  const canDecrease = activeIndex > 0;

  return (
    <>
      <div className="pagerViewContainer" onScroll={onScroll} ref={scrollRef}>
        <div className={`pagerViewContent${highlightActive ? ' highlightActive' : ''}`}>
          <div className="pagerViewPadding" />
          {Children.map( children, ( child, index ) => (
            <div className={`pagerViewChild${highlightActive && index === activeIndex ? ' highlighted' : ''}`}>
              {child}
            </div>
          ) )}
          <div className="pagerViewPadding" />
        </div>
      </div>
      <div className="pagerArrows">
        {useArrows && (
          <img
            src={arrow}
            alt="left"
            className={!canDecrease ? 'disabled' : 'cursorPointer'}
            onClick={() => canDecrease && scroll( activeIndex - 1, true )}
            aria-hidden
          />
        )}
        <div className="pagerViewIndicators">
          {Children.map( children, ( _, index ) => {

            const size = Math.max( 6, 10 - Math.abs( activeIndex - index ) * 2 );

            return (
              <div
                key={`indicator${String( index )}`}
                className={`pagerViewIndicator${activeIndex === index ? ' active' : ''}`}
                style={{ width: size, height: size }}
              />
            );

          } )}
        </div>
        {useArrows && (
          <img
            src={arrow}
            alt="right"
            className={`arrowRight${!canIncrease ? ' disabled' : ' cursorPointer'}`}
            onClick={() => canIncrease && scroll( activeIndex + 1, true )}
            aria-hidden
          />
        )}
      </div>
    </>
  );

};

export default PagerView;
